import { ELEMENTS } from 'Editor/services/consts';
import { uniq } from 'lodash-es';
import { BaseTypedEmitter, RealtimeObject } from '_common/services/Realtime';
import { Structure, DEFAULT_CAPTION } from '../../models';
import BaseController from '../BaseController';

export class Caption extends BaseTypedEmitter<{
  UPDATED: (label: string, data: Editor.Data.Structure.CaptionData) => void;
}> {
  protected _label: string;
  protected _data: Editor.Data.Structure.CaptionData;

  constructor(label: string, data: Editor.Data.Structure.CaptionData) {
    super();
    this._label = label;
    this._data = data;
  }

  get label() {
    return this._label;
  }

  get data() {
    return this._data;
  }

  get numbering() {
    return this._data.nf;
  }

  get separator() {
    return this._data.s;
  }

  get chapter() {
    return this._data.c;
  }

  get labelStyle() {
    return this._data.ls || ELEMENTS.ParagraphElement.BASE_STYLES.FIGURE_CAPTION;
  }

  get position() {
    return this._data.p;
  }

  updateData(data: Editor.Data.Structure.CaptionData) {
    this._data = {
      ...this._data,
      ...data,
    };
    this.emit('UPDATED', this._label, this._data);
  }
}
export class CaptionsController extends BaseController {
  private structure?: Structure;
  private captions: { [label: string]: Caption } = {};

  constructor(Data: Editor.Data.State) {
    super(Data);
    this.handleCaptionAdded = this.handleCaptionAdded.bind(this);
    this.handleCaptionLoad = this.handleCaptionLoad.bind(this);
    this.handleCaptionRemoved = this.handleCaptionRemoved.bind(this);
  }

  start(documentId: string): void {
    this.structure = this.Data.models?.get(
      this.Data?.models.TYPE_NAME.STRUCTURE,
      `DS${documentId}`,
    );

    this.structure?.on('LOADED', this.handleCaptionLoad);
    this.structure?.on('LOAD_CAPTIONS', this.handleCaptionLoad);
    this.structure?.on('ADDED_CAPTION', this.handleCaptionAdded);
    this.structure?.on('UPDATED_CAPTION', this.handleCaptionAdded);
    this.structure?.on('REMOVED_CAPTION', this.handleCaptionRemoved);
    if (this.structure?.loaded) {
      this.handleCaptionLoad();
    }
  }

  stop(): void {}

  destroy(): void {
    const captionsToDestroy = Object.keys(this.captions);

    for (let index = 0; index < captionsToDestroy.length; index++) {
      const key = captionsToDestroy[index];
      this.captions[key].destroy();
    }
  }

  private handleCaptionLoad() {
    let cpt = this.structure?.get([this.structure?.KEYS.CAPTIONS]) || {};
    let newLabels = Object.keys(cpt);
    let oldLabels = Object.keys(this.captions);
    let labels = uniq([...newLabels, ...oldLabels]);
    for (let index = 0; index < labels.length; index++) {
      let label = labels[index];
      if (newLabels.includes(label)) {
        if (this.captions[label]) {
          this.captions[label].updateData(cpt[label]);
        } else {
          this.captions[label] = new Caption(label as string, cpt[label]);
        }
      } else if (oldLabels.includes(label)) {
        delete this.captions[label];
      }
    }
  }

  private handleCaptionAdded(label: string, data: Editor.Data.Structure.CaptionData) {
    if (this.captions[label]) {
      this.captions[label].updateData(data);
    } else {
      this.captions[label] = new Caption(label, data);
    }
  }

  private handleCaptionRemoved(label: string) {
    if (this.captions[label]) {
      delete this.captions[label];
    }
  }

  async createCaption(
    label: string,
    data?: Partial<Editor.Data.Structure.CaptionData>,
  ): Promise<RealtimeObject<Editor.Data.Structure.StructureData> | undefined> {
    const d = {
      ...DEFAULT_CAPTION,
      ...data,
    };
    return this.structure?.set([this.structure?.KEYS.CAPTIONS, label], d);
  }

  async updateCaption(
    label: string,
    data: Partial<Editor.Data.Structure.CaptionData>,
  ): Promise<RealtimeObject<Editor.Data.Structure.StructureData> | undefined> {
    const d = {
      ...DEFAULT_CAPTION,
      ...data,
    };
    return this.structure?.set([this.structure?.KEYS.CAPTIONS, label], d);
  }

  async deleteCaption(
    label: string,
  ): Promise<RealtimeObject<Editor.Data.Structure.StructureData> | undefined> {
    return this.structure?.delete([this.structure?.KEYS.CAPTIONS, label]);
  }

  availableCaptions(): string[] {
    return Object.keys(this.captions);
  }

  caption(label: string): Caption {
    return this.captions[label];
  }
}
